import React, {
    ReactElement, useContext, useEffect, useState,
} from "react";
import { MdNotificationsActive } from "react-icons/md";
import { Permission } from "../__generated__/api";
import usePortal from "./PortalContext";

const LOGO_URL = "https://static.foqal.io/logos/agent/reverse/logo-256.png";
interface NotificationTypes {
    conversation(conversationId: string, title: string, body: string): void;
    message(messageId: string, title: string, body: string): void;
}

export const NotificationContext = React.createContext<NotificationTypes>({
    conversation() {
        // do nothing
    },
    message() {
        // do nothing
    },
});

class Notifications implements NotificationTypes {
    public static readonly instance = new Notifications();

    private conversationAudio?: HTMLAudioElement;

    private messageAudio?: HTMLAudioElement;

    private isLoaded = false;

    // eslint-disable-next-line no-useless-constructor
    private constructor() {
        // empty on purpose
    }

    public ensureLoaded() {
        if (this.isLoaded) {
            return;
        }
        this.conversationAudio = new Audio("https://static.foqal.io/knowledge/beyond.mp3");
        this.conversationAudio.load();

        this.messageAudio = new Audio("https://static.foqal.io/knowledge/bubble.mp3");
        this.messageAudio.load();
        this.isLoaded = true;
    }

    public static get hasPermission(): boolean {
        return Notification.permission === "granted";
    }

    // eslint-disable-next-line class-methods-use-this
    conversation(conversationId: string, title: string, body: string): void {
        this.conversationAudio?.play().catch(() => {
            // do nothing - security error so cant do much on safari.
        });

        if (!Notifications.hasPermission) {
            return;
        }
        // eslint-disable-next-line no-new
        new Notification(title, {
            tag: conversationId,
            body,
            icon: LOGO_URL,
        });
    }

    // eslint-disable-next-line class-methods-use-this
    message(messageId: string, title: string, body: string): void {
        this.messageAudio?.play().catch(() => {
            // do nothing - security error so cant do much on safari.
        });

        if (!Notifications.hasPermission) {
            return;
        }

        // eslint-disable-next-line no-new
        new Notification(title, {
            tag: messageId,
            body,
            icon: LOGO_URL,
        });
    }
}

export function NotificationProvider({ children }: {children: ReactElement}): (ReactElement | null) {
    const [isEnabled, setIsSet] = useState(false);
    const { permissions } = usePortal();

    const hasPermissions = permissions.has(Permission.ChatsModify)
        || permissions.has(Permission.Administrator)
        || permissions.has(Permission.TicketsReadSelf);

    useEffect(() => {
        if (hasPermissions) {
            Notifications.instance.ensureLoaded();
        }
    }, [hasPermissions]);

    if (!hasPermissions) {
        return children;
    }

    const enableNotifications = async () => {
        await Notification.requestPermission();
        setIsSet(true);
    };

    return (
        <NotificationContext.Provider value={Notifications.instance}>
            {(!isEnabled && Notification.permission === "default" && hasPermissions) && (
                <div className="fixed-bottom alert alert-secondary mb-0 d-flex justify-content-between align-items-center">
                    <div>
                        <MdNotificationsActive />
                        {" "}
                        Please enable notifications so that we can let you know when something new comes in.
                    </div>
                    <button
                        onClick={enableNotifications}
                        type="button"
                        className="btn btn-primary btn-sm"
                    >
                        Enable Notifications

                    </button>
                </div>
            )}
            {children}
        </NotificationContext.Provider>
    );
}

export default function useNotifications(): NotificationTypes {
    return useContext(NotificationContext) ?? {};
}
